import { Button, Card, Flex, Skeleton, Table, TableProps } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { snakeCase } from 'utils/snakeCase';

import ListButton from 'components/listButton/listButton';
import NoData from 'components/noData/noData';
import StatusComponent from 'components/status/status';
import TableTotal from 'components/tableTotal/tableTotal';
import ActionListHeaderComponent from 'modules/main/actions/listHeader';
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import Riyal from 'components/riyal/riyal';
import NumberElement from 'components/number/number';

import NotificationService from 'core/services/notification';
import { onGetCompanyServices, onGetServiceCost } from 'modules/main/actions/commonServices';
import { onCancelCapitalRaise, onGetRaiseCapitalsList } from '../services/raiseCapitals';
import { StatusColors } from 'utils/statusColors';
import { onGetAuthorizedCapital } from '../services/authorizedCapital';
import authService from 'core/services/auth';

import { CompanyServiceKey, ServiceCost, ServiceKey } from 'modules/main/actions/commonModels';
import { AuthorizedCapital, RaiseCapitalListItem } from '../models/raiseCapitals';

// hooks
import useCompanySuspended from 'core/hooks/useCompanySuspended';

// components
import SuspendedTooltip from 'components/suspendedTooltip/suspendedTooltip';

const RaiseCapitalsListPage: FunctionComponent = () => {
	const { t } = useTranslation();

	const { suspendedProps } = useCompanySuspended();

	const navigate = useNavigate();
	const location = useLocation();

	const [isRaiseCapitalLoading, setIsRaiseCapitalLoading] = useState<boolean>(true);
	const [isAuthorizedCapitalLoading, setIsAuthorizedCapitalLoading] = useState<boolean>(true);
	const [raiseCapitals, setRaiseCapitals] = useState<RaiseCapitalListItem[]>([]);
	const [authorizedCapital, setAuthorizedCapital] = useState<AuthorizedCapital>();

	const [serviceCostLoading, setServiceCostLoading] = useState<boolean>(true);
	const [ProfitCapitalizationServiceCost, setProfitCapitalizationServiceCost] = useState<ServiceCost>();
	const [PrivateEquityIssuanceServiceCost, setPrivateEquityIssuanceServiceCost] = useState<ServiceCost>();
	const [TreasurySharesAcquisitionServiceCost, setTreasurySharesAcquisitionServiceCost] = useState<ServiceCost>();

	const [selectedRequest, setSelectedRequest] = useState<RaiseCapitalListItem>();
	const [cancelModalOpen, setCancelModalOpen] = useState<boolean>(false);
	const [isCancelling, setIsCancelling] = useState<boolean>(false);

	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);

	const columns: TableProps<RaiseCapitalListItem>['columns'] = [
		{
			title: t('ACTIONS.CODE'),
			dataIndex: 'formated_request_number',
			key: 'formated_request_number',
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'status_key',
			key: 'status_key',
			render: (status_key) => <StatusComponent text={t(`COMMON.${status_key.toUpperCase()}`)} color={StatusColors[status_key]} />,
		},
		{
			title: t('ACTIONS.REQUEST_DATE'),
			dataIndex: 'request_date',
			key: 'request_date',
		},
		{
			title: t('ACTIONS.TYPE'),
			dataIndex: 'type_key',
			key: 'type_key',
			render: (type_key) => <span>{t(`ACTIONS.${snakeCase(type_key)?.toUpperCase()}`)}</span>,
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: '_',
			key: '_',
			render: (_, row) => (
				<Flex gap={10} justify="center">
					<ListButton type="view" text={t('COMMON.VIEW')} onClick={() => navigate(`view/${row.id}`)} />

					{['Scheduled', 'PendingPayment'].includes(row.status_key) && (
						<ListButton type="edit" {...suspendedProps} onClick={() => navigate(`${row.id}`)} text={t('COMMON.EDIT')} />
					)}

					{!['Canceled', 'Completed', 'Failed'].includes(row.status_key) && (
						<ListButton
							type="reject"
							{...suspendedProps}
							onClick={() => {
								setSelectedRequest(row);
								setCancelModalOpen(true);
							}}
							text={t('COMMON.CANCEL')}
						/>
					)}
				</Flex>
			),
		},
	];

	useEffect(() => {
		getServiceCost();
		if (authService.isPermittedToAction('raiseCapital')) getRaiseCapitals();
		if (authService.isPermittedToAction('updateAuthorizedCapital')) getAuthorizedCapital();
	}, []);

	const getCurrentPage = () => {
		const params = new URLSearchParams(location.search);
		const page = params.get('page');
		return page ? parseInt(page) : 1;
	};

	const getServiceCost = async () => {
		try {
			setServiceCostLoading(true);

			const servicesResponse = await onGetCompanyServices();
			const services = servicesResponse?.services?.map((service) => service.key) ?? [];

			const getCost = async (key: CompanyServiceKey & ServiceKey, name: string) => {
				if (!services.includes(key)) return undefined;
				const response = await onGetServiceCost(key);
				return { ...response!.success, name } as ServiceCost;
			};

			const result = await Promise.all([
				getCost('profit_capitalization', 'PROFIT_CAPITALIZATION'),
				getCost('private_equity_issuance', 'PRIVATE_EQUITY_ISSUANCE'),
				getCost('treasury_shares_acquisition', 'TREASURY_SHARES_ACQUISITION'),
			]);

			setProfitCapitalizationServiceCost(result[0]);
			setPrivateEquityIssuanceServiceCost(result[1]);
			setTreasurySharesAcquisitionServiceCost(result[2]);

			setServiceCostLoading(false);
		} catch (error) {
			setServiceCostLoading(false);
		}
	};

	const getAuthorizedCapital = async () => {
		try {
			setIsAuthorizedCapitalLoading(true);

			const response = await onGetAuthorizedCapital();
			setAuthorizedCapital(response.success);

			setIsAuthorizedCapitalLoading(false);
		} catch (error) {
			setIsAuthorizedCapitalLoading(false);
		}
	};

	const getRaiseCapitals = async () => {
		try {
			setIsRaiseCapitalLoading(true);

			const response = await onGetRaiseCapitalsList(1);
			setRaiseCapitals(response.actions.raise_capital_actions);
			setTotalRecords(response.actions.pagination.total);
			setAuthorizedCapital(response.authorized_capital);

			setIsRaiseCapitalLoading(false);
		} catch (error: any) {
			setTotalRecords(0);
			setIsRaiseCapitalLoading(false);
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			const params = new URLSearchParams(location.search);
			params.set('page', page.toString());
			navigate(`${location.pathname}?${params.toString()}`, { replace: true });

			setIsRaiseCapitalLoading(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });

			const response = await onGetRaiseCapitalsList(getCurrentPage());
			setRaiseCapitals(response.actions.raise_capital_actions);
			setTotalRecords(response.actions.pagination.total);

			setCurrentPage(page);
			setIsRaiseCapitalLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsRaiseCapitalLoading(false);
		}
	};

	const cancelHandler = async () => {
		try {
			setIsCancelling(true);
			await onCancelCapitalRaise(selectedRequest?.id as number);
			NotificationService.success(
				t('COMMON.SUCCESS'),
				selectedRequest?.type_key === 'ProfitCapitalization'
					? t('ACTIONS.PROFIT_CAPITALIZATION_CANCELLED')
					: selectedRequest?.type_key === 'TreasurySharesAcquisition'
						? t('ACTIONS.TREASURY_SHARES_ACQUISITION_CANCELLED')
						: t('ACTIONS.PRIVATE_EQUITY_ISSUANCE_CANCELLED'),
			);
			setCancelModalOpen(false);
			setIsCancelling(false);
			getRaiseCapitals();
			setSelectedRequest(undefined);
		} catch (error) {
			setIsCancelling(false);
			NotificationService.error(t('COMMON.ERROR'), t('ACTIONS.CAPITAL_RAISE_NOT_CANCELLED'));
		}
	};

	return (
		<>
			<ActionListHeaderComponent
				title={t('ACTIONS.RAISE_CAPITAL')}
				subTitle={t('ACTIONS.ALLOW_YOU_TO_PERFORM_RAISE_OPERATION')}
				costLoading={serviceCostLoading}
				costs={[ProfitCapitalizationServiceCost, PrivateEquityIssuanceServiceCost, TreasurySharesAcquisitionServiceCost].filter(Boolean) as ServiceCost[]}
			/>

			{isAuthorizedCapitalLoading ? (
				<div className="mb-4">
					<Skeleton.Button active className="w-100" style={{ height: '120px', borderRadius: 'var(--radius)' }} />
				</div>
			) : (
				<Card className="mb-4">
					<Flex align="center" justify="space-between" gap={20} wrap>
						<div>
							<p className="mb-1">{t('ACTIONS.AUTHORIZED_CAPITAL')}</p>
							<Flex align="center" style={{ backgroundColor: '#F9FAFB', border: '1px solid #D0D5DD', padding: '5px', borderRadius: 'var(--radius)' }}>
								<Flex align="center">
									<Flex gap={5} align="center" className="mx-5 fw-semibold" style={{ color: '#555' }}>
										<Riyal size={13} />
										<NumberElement
											style={{ fontSize: 15, fontWeight: 500 }}
											value={+authorizedCapital?.authorized_capital.replaceAll(',', '')!}
											decimalScale={2}
										/>
									</Flex>
									<StatusComponent
										color="blue"
										bordered
										text={
											<Flex gap={5} align="center">
												<span>{t('ACTIONS.ISSUED_CAPITAL_IS')}</span>

												<span className="fw-medium mx-1 d-flex align-items-center gap-1" style={{ direction: 'ltr' }}>
													<Riyal size={13} />
													<NumberElement
														style={{ fontSize: 15, fontWeight: 500 }}
														value={+authorizedCapital?.issued_capital.replaceAll(',', '')!}
														decimalScale={2}
													/>
												</span>
												<span>{t('ACTIONS.WHICH_IS')}</span>

												<span className="fw-medium">{authorizedCapital?.ratio}%</span>

												<span>{t('ACTIONS.OF_AUTHORIZED_CAPITAL')}</span>
											</Flex>
										}
									/>
								</Flex>
							</Flex>
						</div>

						{authService.isPermittedToAction('updateAuthorizedCapital') && (
							<SuspendedTooltip>
								<Button type="primary" size="middle" onClick={() => navigate('authorized-capital')}>
									{t('ACTIONS.INCREASE_AUTHORIZED')}
								</Button>
							</SuspendedTooltip>
						)}
					</Flex>
				</Card>
			)}

			<Table
				columns={columns}
				dataSource={raiseCapitals}
				className="bordered"
				scroll={{ x: 600 }}
				rowKey="id"
				loading={isRaiseCapitalLoading}
				locale={{ emptyText: <NoData /> }}
				pagination={{
					pageSize: 15,
					total: totalRecords,
					showSizeChanger: false,
					current: currentPage,
					onChange: fetchMoreData,
					showTotal: (total) => <TableTotal total={total} />,
				}}
			/>

			{/* CANCEL MODAL */}
			<ConfirmModalComponent
				open={cancelModalOpen}
				confirmText={t('COMMON.CONFIRM')}
				onConfirm={cancelHandler}
				isLoading={isCancelling}
				onDiscard={() => setCancelModalOpen(false)}
				title={
					selectedRequest?.type_key === 'ProfitCapitalization'
						? t('ACTIONS.CANCEL_PROFIT_CAPITALIZATION')
						: selectedRequest?.type_key === 'TreasurySharesAcquisition'
							? t('ACTIONS.CANCEL_TREASURY_SHARES_ACQUISITION')
							: t('ACTIONS.CANCEL_PRIVATE_EQUITY_ISSUANCE')
				}
				description={
					selectedRequest?.type_key === 'ProfitCapitalization'
						? t('ACTIONS.CONFIRM_CANCEL_PROFIT_CAPITALIZATION')
						: selectedRequest?.type_key === 'TreasurySharesAcquisition'
							? t('ACTIONS.CONFIRM_CANCEL_TREASURY_SHARES_ACQUISITION')
							: t('ACTIONS.CONFIRM_CANCEL_PRIVATE_EQUITY_ISSUANCE')
				}
				descriptionClasses="text-center"
			/>
		</>
	);
};

export default RaiseCapitalsListPage;
